import { getApiBaseUrl } from "./config";
import {
  CSRF_ERROR_CODE,
  CSRF_HEADER_NAME,
  MUTATION_METHODS,
  captureCsrfToken,
  getCsrfToken,
} from "./csrf";
import {
  ApiError,
  isActionableFailure,
  messageFromPayload,
  withSupportReference,
  type ApiErrorPayload,
} from "./errors";
import { isRefreshPath, renewSession, type RefreshOutcome } from "./refresh";
import { applyScopeHeaders, withScopeParams } from "./scope-headers";
import { captureDiagnostic, sanitizeDiagnosticText } from "@/lib/diagnostics";

export interface ApiFetchInit extends Omit<RequestInit, "credentials"> {
  /** Internal — set on the retried request so we only refresh once. */
  _retried?: boolean;
  /** Internal — set on the retry that follows a CSRF 403, so we try once. */
  _csrfRetried?: boolean;
}

function methodOf(init?: ApiFetchInit): string {
  return (init?.method ?? "GET").toUpperCase();
}

function buildHeaders(path: string, init?: ApiFetchInit): Headers {
  const headers = new Headers(init?.headers);
  // A FormData body has to name its own multipart boundary, which only fetch
  // can generate — declaring JSON here would leave the server unable to parse it.
  if (
    init?.body &&
    !headers.has("content-type") &&
    !(init.body instanceof FormData)
  ) {
    headers.set("content-type", "application/json");
  }
  if (MUTATION_METHODS.has(methodOf(init)) && !isRefreshPath(path)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers.set(CSRF_HEADER_NAME, csrfToken);
  }
  applyScopeHeaders(headers);
  return headers;
}

let csrfReread: Promise<unknown> | null = null;

/**
 * Re-read the current token once, however many requests discover it stale at
 * the same moment — the single-flight refresh.ts uses, for the same reason.
 */
function rereadCsrfToken(): Promise<unknown> {
  if (!csrfReread) {
    csrfReread = apiFetch("/auth/me")
      .catch(() => undefined)
      .finally(() => {
        csrfReread = null;
      });
  }
  return csrfReread;
}

const JSON_CONTENT_TYPE = /^application\/(?:[\w.-]+\+)?json\b/i;

function isJsonResponse(response: Response): boolean {
  return JSON_CONTENT_TYPE.test(response.headers.get("content-type") ?? "");
}

function nonJsonError(
  response: Response,
  path: string,
  detail: string,
): ApiError {
  return new ApiError(
    `Expected a JSON body from ${path} but ${detail} (HTTP ${response.status}). Check that the API base URL points at the API.`,
    response.status,
    "NON_JSON_RESPONSE",
  );
}

/** Error bodies stay best-effort: a failed request must keep its own status. */
async function parseErrorPayload(response: Response): Promise<ApiErrorPayload> {
  return (await response.json().catch(() => ({}))) as ApiErrorPayload;
}

/**
 * A successful response must carry a JSON body. When the API base URL resolves
 * to the app's own origin, the SPA fallback answers API calls with index.html
 * and HTTP 200; handing that back as `T` surfaces the failure somewhere far
 * from its cause, so it raises here instead.
 */
async function parseBody<T>(response: Response, path: string): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text.length === 0) return undefined as T;
  if (!isJsonResponse(response)) {
    const contentType =
      response.headers.get("content-type")?.split(";")[0]?.trim() || "untyped";
    throw nonJsonError(response, path, `the response was ${contentType}`);
  }
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    throw nonJsonError(response, path, "the body was not valid JSON");
  }
  captureCsrfToken(path, body);
  return body as T;
}

/**
 * Shared client for talking to the Autar API directly from the browser.
 *
 * - `credentials: 'include'` sends the httpOnly access/refresh cookies
 *   cross-subdomain (server derives cookie Domain from API_BASE_URL — see
 *   src/server/utils/cookies.ts).
 * - Adds the `X-CSRF-Token` header on mutating requests, preferring the
 *   readable `__csrf` cookie and falling back to the token the auth routes
 *   echo in their response body — see csrf.ts for which wins and why.
 * - On a 401, refreshes the session exactly once (single-flight — see
 *   refresh.ts) and retries the original request. A second 401 is not
 *   retried again.
 * - On a CSRF 403, re-reads the token and retries the mutation once.
 * - Names the scope of the tab's address as request parameters — see
 *   scope-headers.ts for which requests carry which.
 *
 * `path` is relative to the API root, e.g. `/auth/login` — no `/api` prefix
 * (see CLAUDE.md "Server route paths").
 */
export async function apiFetch<T = unknown>(
  path: string,
  init?: ApiFetchInit,
): Promise<T> {
  const { _retried, _csrfRetried, ...requestInit } = init ?? {};
  const response = await fetch(`${getApiBaseUrl()}${withScopeParams(path)}`, {
    ...requestInit,
    credentials: "include",
    headers: buildHeaders(path, init),
  });

  let refresh: RefreshOutcome | undefined;
  if (response.status === 401 && !_retried && !isRefreshPath(path)) {
    refresh = await renewSession();
    if (refresh === "renewed") {
      return apiFetch<T>(path, { ...init, _retried: true });
    }
  }

  if (!response.ok) {
    const payload = await parseErrorPayload(response);
    // The token rotates on every refresh and only the tab that made the call
    // learns the new value, so a sibling tab can hold a stale one. /auth/me
    // echoes the current token without rotating it, which is what makes a
    // single re-read enough to recover.
    //
    // Replaying a mutation is only safe because csrfProtection is installed
    // (index.middleware.ts) before any router is mounted (index.ts), so a CSRF
    // 403 guarantees no handler ran. Mount a router ahead of it and this retry
    // starts applying mutations twice.
    if (
      response.status === 403 &&
      payload.code === CSRF_ERROR_CODE &&
      !_csrfRetried &&
      MUTATION_METHODS.has(methodOf(init))
    ) {
      await rereadCsrfToken();
      return apiFetch<T>(path, { ...init, _csrfRetried: true });
    }
    const reference = supportReferenceOf(response, payload);
    const message = messageFromPayload(payload, response.status);
    captureDiagnostic({
      level: "error",
      source: "api",
      message,
      details: sanitizeDiagnosticText(payload),
      path,
      status: response.status,
      code: payload.code,
      requestId:
        response.headers.get("x-request-id") ??
        response.headers.get("x-correlation-id") ??
        undefined,
      supportReference: reference,
    });
    throw new ApiError(
      isActionableFailure(response.status, payload.code)
        ? message
        : withSupportReference(message, reference),
      response.status,
      payload.code,
      payload,
      reference,
      refresh === "unavailable",
    );
  }

  return parseBody<T>(response, path);
}

/**
 * The support reference for a failed request.
 *
 * The header is authoritative and is present on every response; the body
 * carries it too, which is what a client behind a proxy that strips headers
 * still sees.
 */
function supportReferenceOf(
  response: Response,
  payload: ApiErrorPayload,
): string | undefined {
  const header = response.headers.get("x-support-reference");
  if (header) return header;
  const fromBody = payload.support_reference;
  return typeof fromBody === "string" ? fromBody : undefined;
}
